import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ReactComponent as PlaneIcon } from "assets/images/iconsV2/plane.svg";
import { ReactComponent as CargoShipIcon } from "assets/images/iconsV2/cargo-ship.svg";
import Badge from "components/v2/Badge/Badge";
import AddressLocodeAndCustomFields from "components/organisms/AddressLocodeAndCustomFields/AddressLocodeAndCustomFields";
import { partyContactKey, resolvePartySyncKind, type PartySyncInput } from "helpers/partySyncKind";
import { getScheduleStopLocationKey } from "../scheduleStopUtils";

type ShippingPartyAddress = {
  name?: string;
  street_address?: string;
  street_address_line_two?: string;
  city_name?: string;
  state_name?: string;
  country_name?: string;
  zipcode?: string;
  un_locode?: string | null;
  location_reference_number?: string | null;
  custom_address_reference?: string | null;
};

type ShippingPartyPort = {
  name?: string;
  code?: string;
  iata?: string;
  fs?: string;
  un_locode?: string;
  city_name?: string;
  country_name?: string;
};

export type ShippingParty = {
  same_as_delivery_address_contact?: boolean;
  same_as_address_contact?: string;
  form_address_contact_type?: string;
  address_type?: string;
  is_port_address?: boolean;
  address_reference_number?: string | null;
  address?: ShippingPartyAddress | null;
  airport?: ShippingPartyPort | string | null;
  seaport?: ShippingPartyPort | string | null;
  airport_name?: string;
  seaport_name?: string;
  address_name?: string;
  address_street?: string;
  address_street_line2?: string;
  address_city?: string;
  address_state?: string;
  address_country?: string;
  address_zipcode?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
};

function hasPartyText(value?: string | null) {
  return Boolean(value && String(value).trim());
}

function formatPartyPortLabel(port?: ShippingPartyPort | string | null, fallbackName?: string): string | undefined {
  if (typeof port === "string") {
    const code = port.trim();
    const name = (fallbackName ?? "").trim();
    if (!code && !name) return undefined;
    if (!code) return name;
    if (!name) return code;
    return `${code} - ${name}`;
  }

  const code = (port?.code || port?.iata || port?.fs || port?.un_locode || "").trim();
  const place = (port?.city_name || port?.name || fallbackName || "").trim();
  const country = (port?.country_name || "").trim();
  const withCountry =
    country && place && !place.toLowerCase().includes(country.toLowerCase()) ? `${place}, ${country}` : place;
  if (!code && !withCountry) return undefined;
  if (!code) return withCountry;
  if (!withCountry) return code;
  return `${code} - ${withCountry}`;
}

function formatPartyStreetBlock(party: ShippingParty): string {
  const parts: string[] = [];
  const street = party.address?.street_address || party.address_street;
  const street2 = party.address?.street_address_line_two || party.address_street_line2;
  const city = party.address?.city_name || party.address_city;
  const state = party.address?.state_name || party.address_state;
  const zip = party.address?.zipcode || party.address_zipcode;
  const country = party.address?.country_name || party.address_country;
  if (street) parts.push(street);
  if (street2) parts.push(street2);
  if (city) parts.push(city);
  if (state) parts.push(state);
  if (zip) parts.push(zip);
  if (country) parts.push(country);
  return parts.join(", ");
}

function shouldHidePortDuplicateStreet(party: ShippingParty, portLabel?: string): boolean {
  if (!party.is_port_address) return false;
  const street = (party.address?.street_address || party.address_street || "").trim();
  if (!street) return false;
  const normalizedStreet = street.toLowerCase();
  const candidates = [
    party.address?.name,
    party.address_name,
    portLabel,
    party.airport_name,
    party.seaport_name,
  ]
    .filter(hasPartyText)
    .map((value) => String(value).trim().toLowerCase());
  return candidates.includes(normalizedStreet);
}

export function partyHasAddressDetails(party?: ShippingParty): boolean {
  if (!party) return false;
  if (
    hasPartyText(party.address?.name) ||
    hasPartyText(party.address?.street_address) ||
    hasPartyText(party.address?.city_name)
  ) {
    return true;
  }
  if (typeof party.airport === "string" ? hasPartyText(party.airport) : Boolean(party.airport?.code || party.airport?.name)) {
    return true;
  }
  if (
    typeof party.seaport === "string" ? hasPartyText(party.seaport) : Boolean(party.seaport?.un_locode || party.seaport?.name)
  ) {
    return true;
  }
  return Boolean(
    party.address_name ||
      party.address_street ||
      party.airport_name ||
      party.seaport_name ||
      party.address_reference_number,
  );
}

function asText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function getPortCode(port?: ShippingPartyPort | string | null): string {
  if (typeof port === "string") return asText(port);
  return asText(port?.code) || asText(port?.iata) || asText(port?.fs) || asText(port?.un_locode);
}

export function getShippingPartyLocationKey(party?: ShippingParty): string {
  if (!party) return "";
  const addressType = String(party.address_type ?? "").toLowerCase();
  if (addressType === "airport") {
    const code = getPortCode(party.airport);
    return code ? `airport:${code}` : "";
  }
  if (addressType === "seaport" || addressType === "port") {
    const code = getPortCode(party.seaport);
    return code ? `seaport:${code}` : "";
  }
  const reference = asText(party.address_reference_number);
  return reference ? `address:${reference}` : "";
}

function toDetailsPartyInput(party?: ShippingParty): PartySyncInput | undefined {
  if (!party) return undefined;
  const formType = String(party.form_address_contact_type ?? "").toLowerCase();
  const sameAsOption = party.same_as_address_contact;
  return {
    locationKey: getShippingPartyLocationKey(party),
    contactKey: partyContactKey(party.contact_name, party.contact_phone, party.contact_email),
    sameAsDelivery:
      Boolean(party.same_as_delivery_address_contact) ||
      formType === "delivery" ||
      sameAsOption === "sameAsDeliveryAddressContact",
    sameAsConsignee: formType === "consignee" || sameAsOption === "sameAsConsigneeAddressContact",
    allowLocationInference: !formType && !sameAsOption,
  };
}

export function toDetailsDeliveryInput(stop: unknown): PartySyncInput {
  const raw = (stop ?? {}) as Record<string, unknown>;
  return {
    locationKey: getScheduleStopLocationKey(stop),
    contactKey: partyContactKey(
      asText(raw.contact_name ?? raw.stop_contact_name),
      asText(raw.contact_phone ?? raw.stop_contact_number),
      asText(raw.contact_email ?? raw.stop_contact_email),
    ),
  };
}

export type PartySyncContext = {
  role?: "consignee" | "notify";
  consignee?: ShippingParty;
  delivery?: PartySyncInput;
};

export function getPartySyncKind(party: ShippingParty, context?: PartySyncContext): "delivery" | "consignee" | null {
  const partyInput = toDetailsPartyInput(party);
  if (!partyInput) return null;
  return resolvePartySyncKind(partyInput, context?.delivery ?? { locationKey: "", contactKey: "" }, {
    role: context?.role,
    consignee: toDetailsPartyInput(context?.consignee),
  });
}

function formatContactLine(phone?: string, email?: string): string {
  return [phone, email].filter(Boolean).join(" | ");
}

export function PartyContact({
  testId,
  label,
  party,
  syncContext,
}: {
  testId: string;
  label: ReactNode;
  party?: ShippingParty;
  syncContext?: PartySyncContext;
}) {
  const intl = useIntl();
  if (!party) return null;

  const hasContact = Boolean(party.contact_name || party.contact_phone || party.contact_email);
  const hasAddress = partyHasAddressDetails(party);
  const syncKind = getPartySyncKind(party, syncContext);
  if (!hasContact && !hasAddress && !syncKind) return null;

  const addressType = String(party.address_type ?? "").toLowerCase();
  const isAirport = addressType === "airport" || Boolean(party.airport);
  const isSeaport = addressType === "seaport" || addressType === "port" || Boolean(party.seaport);
  const portLabel = isAirport
    ? formatPartyPortLabel(party.airport, party.airport_name)
    : isSeaport
      ? formatPartyPortLabel(party.seaport, party.seaport_name)
      : undefined;
  const hideDuplicatePortStreet = shouldHidePortDuplicateStreet(party, portLabel);
  const addressName = party.address?.name || party.address_name;
  const streetBlock = formatPartyStreetBlock(party);
  const contactLine = formatContactLine(party.contact_phone, party.contact_email);

  if (syncKind) {
    const syncMessage =
      syncKind === "delivery" ? (
        <FormattedMessage
          id="request-form.review.schedule.sameAsDelivery"
          defaultMessage="Same as delivery address and contact"
        />
      ) : (
        <FormattedMessage
          id="new-request.schedule.sameAsConsigneeAddressContact"
          defaultMessage="Same as consignee address & consignee contact"
        />
      );

    return (
      <div className="rfv2-review__stop-row" data-testid={testId}>
        <div className="rfv2-review__stop-location">
          <div className="rfv2-review__stop-top">
            <span className="rfv2-review__field-label">{label}</span>
          </div>
          <span className="rfv2-review__field-value">{syncMessage}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rfv2-review__stop-row" data-testid={testId}>
      <div className="rfv2-review__stop-location">
        <div className="rfv2-review__stop-top">
          <span className="rfv2-review__field-label">{label}</span>
        </div>

        <div className="rfv2-review__stop-address">
          {(isAirport || isSeaport) && portLabel && (
            <div className="rfv2-review__stop-address-type">
              <Badge
                label={
                  isAirport
                    ? intl.formatMessage({ id: "request-form.review.schedule.airport", defaultMessage: "Airport" })
                    : intl.formatMessage({ id: "request-form.review.schedule.port", defaultMessage: "Port" })
                }
                color="gray"
                type="status-badge"
                variant="icon-leading"
                size="sm"
                icon={isAirport ? <PlaneIcon width={16} height={16} /> : <CargoShipIcon width={16} height={16} />}
              />
              <span className="rfv2-review__field-value">{portLabel}</span>
            </div>
          )}
          {addressName && !hideDuplicatePortStreet && <div className="rfv2-review__field-value">{addressName}</div>}
          {streetBlock && !hideDuplicatePortStreet && <div className="rfv2-review__field-value">{streetBlock}</div>}
          {(party.address?.un_locode || party.address?.custom_address_reference) && (
            <AddressLocodeAndCustomFields
              un_locode={party.address?.un_locode || undefined}
              custom_address_reference={party.address?.custom_address_reference || undefined}
            />
          )}
        </div>
      </div>

      <div className="rfv2-review__stop-contact">
        <span className="rfv2-review__field-label">
          {intl.formatMessage({ id: "request-form.review.schedule.contact", defaultMessage: "Contact" })}
        </span>
        <span className="rfv2-review__field-value">{party.contact_name || "-"}</span>
        {contactLine && <span className="rfv2-review__stop-meta">{contactLine}</span>}
      </div>
    </div>
  );
}
